/**
 * Comparison-Table Template: Side-by-side item comparison
 *
 * This template provides a coordinated comparison view where:
 * - Items are displayed in columns
 * - Specs are aligned in rows for easy comparison
 * - Differences can be highlighted
 * - Actions available for each item or overall
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper, disableButtonTemporarily, handleImageError } from '../utils';
import { navigateToUrl } from '../../../core/navigation';
import type { ComparisonTableData, ComparisonItem, TemplateAction } from './types';

// =============================================================================
// COMPARISON-TABLE TEMPLATE
// =============================================================================

registerBlockRenderer('comparison-table', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const templateData = data as ComparisonTableData;

  if (!templateData.items || templateData.items.length < 2) {
    console.warn('[comparison-table] Need at least 2 items to compare');
    return;
  }

  // Limit to 4 items max
  const items = templateData.items.slice(0, 4);

  const wrapper = createBlockWrapper('comparison-table');
  wrapper.classList.add('pms-template-comparison-table');

  // Title
  if (templateData.title) {
    const title = document.createElement('h3');
    title.className = 'pms-comparison-title';
    title.textContent = templateData.title;
    wrapper.appendChild(title);
  }

  // Build comparison table
  const table = document.createElement('div');
  table.className = 'pms-comparison-grid';
  table.style.setProperty('--comparison-columns', String(items.length));

  // Header row with item images/titles
  const headerRow = document.createElement('div');
  headerRow.className = 'pms-comparison-header-row';

  for (const item of items) {
    const cell = renderItemHeader(item, onAction);
    headerRow.appendChild(cell);
  }
  table.appendChild(headerRow);

  // Collect all unique spec keys across items
  const allSpecs = new Set<string>();
  for (const item of items) {
    Object.keys(item.specs).forEach(key => allSpecs.add(key));
  }

  // Spec rows
  for (const specKey of allSpecs) {
    const row = document.createElement('div');
    row.className = 'pms-comparison-row';

    // Check if values differ (for highlighting)
    const values = items.map(item => item.specs[specKey] || '-');
    const allSame = values.every(v => v === values[0]);
    const shouldHighlight = templateData.highlightDifferences && !allSame;

    for (let i = 0; i < items.length; i++) {
      const cell = document.createElement('div');
      cell.className = 'pms-comparison-cell';
      if (shouldHighlight) {
        cell.classList.add('pms-comparison-highlight');
      }

      const item = items[i];
      const value = item?.specs[specKey] || '-';
      cell.innerHTML = `
        <span class="pms-comparison-spec-label">${escapeHtml(specKey)}</span>
        <span class="pms-comparison-spec-value">${escapeHtml(value)}</span>
      `;
      row.appendChild(cell);
    }

    table.appendChild(row);
  }

  // Price row (if any items have prices)
  const hasPrices = items.some(item => item.price);
  if (hasPrices) {
    const priceRow = document.createElement('div');
    priceRow.className = 'pms-comparison-row pms-comparison-price-row';

    for (const item of items) {
      const cell = document.createElement('div');
      cell.className = 'pms-comparison-cell pms-comparison-price-cell';
      cell.innerHTML = `
        <span class="pms-comparison-spec-label">Price</span>
        <span class="pms-comparison-price">${item.price ? escapeHtml(item.price) : '-'}</span>
      `;
      priceRow.appendChild(cell);
    }

    table.appendChild(priceRow);
  }

  // Action buttons row (per item)
  const hasActions = items.some(item => item.action);
  if (hasActions) {
    const actionRow = document.createElement('div');
    actionRow.className = 'pms-comparison-row pms-comparison-action-row';

    for (const item of items) {
      const cell = document.createElement('div');
      cell.className = 'pms-comparison-cell';

      if (item.action) {
        const btn = document.createElement('button');
        btn.className = 'pms-comparison-item-btn';
        btn.textContent = 'Select';
        btn.addEventListener('click', () => {
          disableButtonTemporarily(btn, 2000);
          if (onAction) {
            onAction(item.action!);
          }
        });
        cell.appendChild(btn);
      }

      actionRow.appendChild(cell);
    }

    table.appendChild(actionRow);
  }

  wrapper.appendChild(table);

  // Global actions (if provided)
  if (templateData.actions && templateData.actions.length > 0) {
    const actionsSection = renderActions(templateData.actions, onAction);
    wrapper.appendChild(actionsSection);
  }

  container.appendChild(wrapper);
});

function renderItemHeader(item: ComparisonItem, _onAction?: (value: string) => void): HTMLElement {
  const cell = document.createElement('div');
  cell.className = 'pms-comparison-header-cell';

  let html = '';

  if (item.badge) {
    html += `<span class="pms-comparison-badge">${escapeHtml(item.badge)}</span>`;
  }

  if (item.image) {
    html += `<div class="pms-comparison-image-wrapper">
      <img class="pms-comparison-image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" />
    </div>`;
  }

  html += `<h4 class="pms-comparison-item-title">${escapeHtml(item.title)}</h4>`;

  cell.innerHTML = html;

  // Handle image error
  const img = cell.querySelector('.pms-comparison-image') as HTMLImageElement;
  if (img) {
    handleImageError(img, false);
  }

  return cell;
}

function renderActions(actions: TemplateAction[], onAction?: (value: string) => void): HTMLElement {
  const section = document.createElement('div');
  section.className = 'pms-comparison-actions';

  for (const action of actions) {
    const btn = document.createElement('button');
    btn.className = 'pms-comparison-action-btn';
    if (action.variant === 'primary') {
      btn.classList.add('pms-comparison-action-primary');
    }
    btn.textContent = action.label;

    btn.addEventListener('click', () => {
      disableButtonTemporarily(btn, 2000);
      if (action.url) {
        navigateToUrl(action.url);
      } else if (action.action.startsWith('link:')) {
        navigateToUrl(action.action.substring(5));
      } else if (onAction) {
        onAction(action.action);
      }
    });

    section.appendChild(btn);
  }

  return section;
}
